import { useEffect, useRef, useState } from 'react';
import emailjs from '@emailjs/browser';
import { contact, images, startLinkProps } from '../data';

const EMAIL_FORMAT = /^([a-zA-Z0-9._%-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})$/;
const NAME_FORMAT = /^[A-Za-z]+$/;

const emptyForm = {
  firstName: '',
  lastName: '',
  email: '',
  message: '',
};

const emptyValidity = {
  firstName: false,
  lastName: false,
  email: false,
  message: false,
};

export default function Contact() {
  const formRef = useRef(null);
  const firstNameRef = useRef(null);
  const lastNameRef = useRef(null);
  const emailRef = useRef(null);
  const messageRef = useRef(null);
  const closeButtonRef = useRef(null);

  const [formData, setFormData] = useState(emptyForm);
  const [formInputsValid, setFormInputsValid] = useState(emptyValidity);
  const [sending, setSending] = useState(false);
  const [popup, setPopup] = useState({ open: false, success: false });

  function closePopup() {
    setPopup({ open: false, success: false });
  }

  useEffect(() => {
    if (!popup.open) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        closePopup();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    closeButtonRef.current?.focus();

    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [popup.open]);

  function setFieldError(ref, isError) {
    if (!ref.current) return;
    ref.current.classList.toggle('input-error', isError);
  }

  function validateField(name, value, isActive) {
    let isValid = false;

    if (name === 'firstName' || name === 'lastName') {
      isValid = NAME_FORMAT.test(value);
      if (!isValid && isActive) {
        setFieldError(name === 'firstName' ? firstNameRef : lastNameRef, true);
      } else if (isValid) {
        setFieldError(name === 'firstName' ? firstNameRef : lastNameRef, false);
      }
    }

    if (name === 'email') {
      isValid = EMAIL_FORMAT.test(value);
      if (!isValid && isActive) {
        setFieldError(emailRef, true);
      } else if (isValid) {
        setFieldError(emailRef, false);
      }
    }

    if (name === 'message') {
      isValid = value.trim().length > 0;
      if (!isValid && isActive) {
        setFieldError(messageRef, true);
      } else if (isValid) {
        setFieldError(messageRef, false);
      }
    }

    setFormInputsValid((prev) => ({ ...prev, [name]: isValid }));
  }

  function handleChange(event) {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    validateField(name, value, document.activeElement === event.target);
  }

  function showPopup(success) {
    setPopup({ open: true, success });
  }

  function markEmptyErrors() {
    if (!formData.firstName.trim()) setFieldError(firstNameRef, true);
    if (!formData.lastName.trim()) setFieldError(lastNameRef, true);
    if (!formData.email.trim()) setFieldError(emailRef, true);
    if (!formData.message.trim()) setFieldError(messageRef, true);
  }

  function sendEmail(event) {
    event.preventDefault();

    const isValid =
      formInputsValid.firstName &&
      formInputsValid.lastName &&
      formInputsValid.email &&
      formInputsValid.message;

    if (!isValid) {
      markEmptyErrors();
      return;
    }

    setSending(true);

    const formElement = formRef.current;
    if (!formElement) {
      setSending(false);
      showPopup(false);
      return;
    }

    emailjs
      .sendForm(
        process.env.REACT_APP_SERVICE_ID,
        process.env.REACT_APP_TEMPLATE_ID,
        formElement,
        {
          publicKey: process.env.REACT_APP_PUBLIC_KEY,
        }
      )
      .then(() => {
        setFormData(emptyForm);
        setFormInputsValid(emptyValidity);
        setFieldError(firstNameRef, false);
        setFieldError(lastNameRef, false);
        setFieldError(emailRef, false);
        setFieldError(messageRef, false);
        showPopup(true);
      })
      .catch((error) => {
        console.error('EmailJS error:', error);
        showPopup(false);
      })
      .finally(() => {
        setSending(false);
      });
  }

  return (
    <section className="section contact" id="contact">
      <div className="container contact-grid">
        <div className="contact-main" data-reveal>
          <div className="contact-intro">
            <p className="eyebrow">Get in touch</p>
            <h2 className="display-2">
              Let’s talk about <em>your books.</em>
            </h2>
            <p className="lede">
              Tell us a little about your business and what you need. We'll take a look and determine
              the right place to start.
            </p>
          </div>

          <div className="contact-form-shell">
            <form
              className="contact-form"
              id="rime-contact-form"
              ref={formRef}
              onSubmit={sendEmail}
              noValidate
            >
              <div className="contact-form-row contact-form-row-split">
                <label className="contact-field">
                  <span className="contact-field-label">First name</span>
                  <input
                    ref={firstNameRef}
                    className="contact-input firstNameField"
                    type="text"
                    name="firstName"
                    id="firstName"
                    autoComplete="given-name"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                  />
                </label>
                <label className="contact-field">
                  <span className="contact-field-label">Last name</span>
                  <input
                    ref={lastNameRef}
                    className="contact-input lastNameField"
                    type="text"
                    name="lastName"
                    id="lastName"
                    autoComplete="family-name"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                  />
                </label>
              </div>

              <label className="contact-field">
                <span className="contact-field-label">Email</span>
                <input
                  ref={emailRef}
                  className="contact-input emailField"
                  type="email"
                  name="email"
                  id="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </label>

              <label className="contact-field">
                <span className="contact-field-label">Message</span>
                <textarea
                  ref={messageRef}
                  className="contact-input contact-textarea messageField"
                  name="message"
                  id="message"
                  rows="4"
                  value={formData.message}
                  onChange={handleChange}
                  required
                />
              </label>
            </form>

            {popup.open && (
              <div
                className="contact-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="contact-modal-title"
                onClick={closePopup}
              >
                <div
                  className={`contact-modal-card${popup.success ? ' is-success' : ' is-error'}`}
                  onClick={(event) => event.stopPropagation()}
                >
                  <button
                    ref={closeButtonRef}
                    className="contact-modal-close"
                    type="button"
                    aria-label="Close message"
                    onClick={closePopup}
                  >
                    ×
                  </button>
                  <p className="contact-modal-title" id="contact-modal-title">
                    {popup.success ? 'Message sent' : 'Something went wrong'}
                  </p>
                  <p className="contact-modal-copy">
                    {popup.success
                      ? "Thanks for reaching out. We'll be in touch soon."
                      : 'Please check your details and try again.'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="contact-panel" data-reveal>
          <figure className="contact-media">
            <img
              src={images.contact}
              alt="A laptop and notebook on a desk beside a window"
              loading="lazy"
              decoding="async"
            />
          </figure>

          <div className="contact-aside">
            <div className="contact-meta">
              <div className="contact-meta-item">
                <p className="contact-meta-label">Phone</p>
                <a className="contact-meta-value" href={contact.phoneHref}>
                  {contact.phone}
                </a>
              </div>
              <div className="contact-meta-item">
                <p className="contact-meta-label">Location</p>
                <p className="contact-meta-value">{contact.location}</p>
              </div>
            </div>

            <p className="contact-aside-label">Or reserve a spot on our calendar</p>
          </div>
        </div>

        <div className="contact-form-actions">
          <button
            className="btn btn-gold"
            type="submit"
            form="rime-contact-form"
            disabled={sending}
          >
            {sending ? 'Sending…' : 'Send message'}
          </button>
        </div>

        <div className="contact-schedule">
          <a className="btn btn-primary" {...startLinkProps}>
            Schedule a call
          </a>
        </div>
      </div>
    </section>
  );
}
